import { jsx as _jsx } from "react/jsx-runtime";
/* eslint-disable react/display-name */
import React, { useEffect, useRef, useState } from 'react';
import { publishEvents, subscribeEvents } from '../Events';
import placements from '../Placements';
import { getTrackingLibrary, setupTracking, trackEvent, } from '../utils/tracking';
import { hasFrequencyCapping } from '../utils/frequencyCapping';
import { Logger } from '../utils/logger';
import { ContentCardProvider, } from './ContentCardProvider';
const BrazeContext = React.createContext({});
const BrazeProvider = ({ children, transportLayer, section, }) => {
    // List of consumers subscribed.
    const consumers = new Map();
    const unsubscribe = useRef(() => { });
    const trackingLibrary = getTrackingLibrary(transportLayer);
    const [contentCards, setContentCards] = useState({
        status: 'waiting',
        cards: [],
    });
    const subscribeConsumer = (type, callback) => {
        var _a;
        consumers.has(type) || consumers.set(type, new Set());
        (_a = consumers.get(type)) === null || _a === void 0 ? void 0 : _a.add(callback);
        return () => {
            const consumer = consumers.get(type);
            if (consumer) {
                consumer.delete(callback);
                if (consumer.size === 0) {
                    consumers.delete(type);
                }
            }
        };
    };
    const isMatchingPlacement = (type, placement) => {
        switch (type) {
            case subscribeEvents.IN_APP_MESSAGE:
                // Weird typecast? Have a look at https://github.com/microsoft/TypeScript/issues/26255 ...
                const iamPlacements = [
                    placements.PRESCREEN,
                    placements.SLIDE_UP,
                ];
                return iamPlacements.includes(placement);
            case subscribeEvents.CONTENT_CARDS:
                return placement === placements.TEASER;
            default:
                return false;
        }
    };
    const hasMatchingSection = (messageSection) => [section, 'all'].includes(messageSection) ||
        (messageSection === undefined && section === 'wbm/Startpage_LI'); // Legacy case
    const isEligibleInAppMessage = (iam) => {
        var _a, _b, _c;
        const frequencyCapped = hasFrequencyCapping(section, (_a = iam.extras) === null || _a === void 0 ? void 0 : _a.placement, (_b = iam === null || iam === void 0 ? void 0 : iam.extras) === null || _b === void 0 ? void 0 : _b.ignore_capping);
        if (frequencyCapped) {
            Logger.log(`BrazeProvider - frequencyCapping applied`);
            return false;
        }
        return isMatchingPlacement(subscribeEvents.IN_APP_MESSAGE, (_c = iam.extras) === null || _c === void 0 ? void 0 : _c.placement);
    };
    const isEligibleCardMessage = (card, cardType) => {
        var _a, _b;
        return (hasMatchingSection((_a = card.extras) === null || _a === void 0 ? void 0 : _a.section) &&
            isMatchingPlacement(cardType, (_b = card.extras) === null || _b === void 0 ? void 0 : _b.placement));
    };
    const isControlCardMessage = (card) => {
        return card.extras && card.extras.control;
    };
    const trackControlMessageImpression = (triggerId) => {
        transportLayer.publish('stm:braze', { type: publishEvents.IN_APP_MESSAGE_SHOWN, campaignId: triggerId }, { fireAndForget: true });
        setupTracking(transportLayer);
        trackEvent('brazeImpression', {
            brazePlacement: placements.CONTROL_MESSAGE,
            brazeTriggerID: triggerId,
        });
    };
    const trackControlCardImpression = (triggerId) => {
        transportLayer.publish('stm:braze', { type: publishEvents.CONTENT_CARD_SHOWN, campaignId: triggerId }, { fireAndForget: true });
        setupTracking(transportLayer);
        trackEvent('brazeImpression', {
            brazePlacement: placements.CONTROL_MESSAGE,
            brazeTriggerID: triggerId,
        });
    };
    const handleIncomingMessage = (message) => {
        var _a;
        Logger.log('BrazeProvider - incoming message', message);
        let selectedAd;
        if (message.type === subscribeEvents.IN_APP_MESSAGE) {
            const iam = message.data;
            if (iam.isControlMessage) {
                trackControlMessageImpression(iam.triggerId);
            }
            selectedAd = isEligibleInAppMessage(iam) ? iam : undefined;
        }
        else if (message.type === subscribeEvents.CONTENT_CARDS) {
            const cards = message.data;
            setContentCards({
                status: 'received',
                cards: message.data,
            });
            const incomingAds = cards.filter((card) => {
                return isEligibleCardMessage(card, message.type);
            });
            selectedAd = incomingAds[Math.floor(Math.random() * incomingAds.length)];
            if (selectedAd && isControlCardMessage(selectedAd)) {
                trackControlCardImpression(selectedAd.id);
                selectedAd = null;
            }
        }
        if (selectedAd) {
            Logger.log('BrazeProvider - selectedAd', selectedAd);
            (_a = consumers.get(selectedAd.extras.placement)) === null || _a === void 0 ? void 0 : _a.forEach((cb) => {
                return cb(selectedAd, section);
            });
        }
    };
    useEffect(() => {
        const subscribeTransportLayer = async () => {
            unsubscribe.current = await transportLayer.subscribe('stm:braze', handleIncomingMessage);
        };
        subscribeTransportLayer();
        return () => {
            unsubscribe.current();
        };
    }, []);
    return (_jsx(BrazeContext.Provider, { value: { subscribeConsumer, trackingLibrary }, children: _jsx(ContentCardProvider, { contentCardState: contentCards, children: children }) }));
};
export { BrazeProvider, BrazeContext };
//# sourceMappingURL=BrazeProvider.js.map